/**
 * The control sheet's drawn icon set — one entry per affordance a device sheet
 * shows (name → inner SVG markup, 24×24 grid).
 *
 * Lifted verbatim from the sketch `docs/plans/device-control-sheet-preview.html`
 * (the `ICONS` + `GROUPS` objects in its script) — the sketch is the reviewed
 * drawing, so a name here is a name there, and paths that had to be refined in
 * the app are noted in the commit that refined them. Names drawn in the app
 * rather than the sketch follow, and the sketch predates all of them, so do not
 * hunt for them there:
 *   * the blinds row's `rotate-ccw`/`rotate-cw` (plan §5.5 — the owner's
 *     venetian-blinds addition, card t_687d7054);
 *   * `spinner` (the P11 sweep, card t_bd3aea71 — a control body still showed
 *     the Material `progress_activity` while loading and the drawn set had no
 *     loading glyph);
 *   * `toggle`, `refresh`, `up-down`, `tap`, `link`, `person-off` and `moon`
 *     (card t_a8249a54 — the in-sheet automation & sun panels drew Material
 *     `switch`/`repeat`/`swap_vert`/`touch_app`/`link`/`person_off`/`bedtime`/
 *     `restart_alt`, and the drawn set had no honest name for a physical
 *     switch, a cycle/reset, up/down buttons, a press, a follow link, absence
 *     or sleep. Drawn once here, the way `spinner` was.)
 * Render through `ControlIcon.vue`, which owns the stroke rules (1.75, round
 * caps/joins, `currentColor`); never inline a path into a widget again.
 *
 * Add an icon by adding it to a group AND to `CONTROL_ICONS` — the unit test
 * (`controlIcons.test.ts`) fails on either half being missing.
 */

export const CONTROL_ICONS: Record<string, string> = {
  power: '<path d="M12 3.4v7.4"/><path d="M17.5 6.6a8 8 0 1 1-11 0"/>',
  'power-off': '<path d="M12 3.4v7.4"/><path d="M17.5 6.6a8 8 0 1 1-11 0"/><path d="M4.7 4.7l14.6 14.6"/>',
  plus: '<path d="M12 5.8v12.4"/><path d="M5.8 12h12.4"/>',
  minus: '<path d="M5.8 12h12.4"/>',
  'chevron-up': '<path d="M7.4 14.2 12 9.6l4.6 4.6"/>',
  'chevron-down': '<path d="M7.4 9.8 12 14.4l4.6-4.6"/>',
  gear: '<circle cx="12" cy="12" r="6.9"/><circle cx="12" cy="12" r="2.6"/><path d="M12 2.7v3.1M12 18.2v3.1M2.7 12h3.1M18.2 12h3.1M5.4 5.4l2.2 2.2M16.4 16.4l2.2 2.2M18.6 5.4l-2.2 2.2M7.6 16.4l-2.2 2.2"/>',
  close: '<path d="M7.2 7.2l9.6 9.6M16.8 7.2l-9.6 9.6"/>',
  bulb: '<path d="M12 3.4a5.8 5.8 0 0 1 3.4 10.5c-.8.6-1.2 1.4-1.3 2.4h-4.2c-.1-1-.5-1.8-1.3-2.4A5.8 5.8 0 0 1 12 3.4z"/><path d="M10 19.2h4"/><path d="M10.9 21.3h2.2"/>',
  warm: '<circle cx="12" cy="13.2" r="3.3"/><path d="M12 6.6V4.6M6.9 9.3 5.5 7.9M17.1 9.3l1.4-1.4M5 13.2H3M21 13.2h-2"/><path d="M5.6 19h12.8"/>',
  snowflake:
    '<path d="M12 3.4v17.2"/><path d="M4.6 7.7l14.8 8.6"/><path d="M19.4 7.7 4.6 16.3"/><path d="M9.9 5.3 12 3.4l2.1 1.9M9.9 18.7 12 20.6l2.1-1.9"/>',
  palette:
    '<path d="M12 3.6a8.4 8.4 0 1 0 0 16.8h1.1a2.2 2.2 0 0 0 1.6-3.7 2.2 2.2 0 0 1 1.6-3.8h1.3a4.4 4.4 0 0 0 4.4-4.4C21.9 6.4 17.5 3.6 12 3.6z"/><path d="M8.2 9.4h.01M12.2 7.4h.01M16 9.2h.01M8.4 14h.01"/>',
  sparkles:
    '<path d="M11.5 5.2l1.5 4 4 1.5-4 1.5-1.5 4-1.5-4-4-1.5 4-1.5z"/><path d="M18.2 14.4l.75 2 2 .75-2 .75-.75 2-.75-2-2-.75 2-.75z"/>',
  'cover-open': '<path d="M6.4 4.6h11.2"/><path d="M12 19.8V9.6"/><path d="M7.9 13.7 12 9.6l4.1 4.1"/>',
  'cover-close': '<path d="M6.4 19.4h11.2"/><path d="M12 4.2v10.2"/><path d="M7.9 10.3 12 14.4l4.1-4.1"/>',
  shade:
    '<rect x="4.8" y="4.4" width="14.4" height="15.2" rx="2.4"/><path d="M4.8 10.2h14.4"/><path d="M4.8 15h14.4" opacity=".55"/>',
  stop: '<rect x="7.5" y="7.5" width="9" height="9" rx="2.2"/>',
  tilt: '<path d="M5.2 9.4l13.6-2.2"/><path d="M4.6 13.8h14.8"/><path d="M5.2 18.2l13.6 2.2"/>',
  // The blinds row's slat arrows (plan §5.5): ring open under the head, head at
  // the upper-left / upper-right pointing along the rotation it stands for.
  'rotate-ccw': '<path d="M6.11 15.8a6.8 6.8 0 1 0 2.49-9.29"/><path d="M11.57 6.93 8.6 6.51l1.12-2.78"/>',
  'rotate-cw': '<path d="M17.89 15.8a6.8 6.8 0 1 1-2.49-9.29"/><path d="M12.43 6.93 15.4 6.51l-1.12-2.78"/>',
  thermometer:
    '<path d="M10.2 4.8a1.8 1.8 0 0 1 3.6 0v8.6a4.2 4.2 0 1 1-3.6 0z"/><path d="M14.6 8.6h2M14.6 11.4h2"/><path d="M12 17.4v-4.6"/>',
  flame:
    '<path d="M12 3.8c.9 2.6 4.4 4.7 4.4 8.1a4.4 4.4 0 0 1-8.8 0c0-1.2.4-2.3 1-3.2.4 1 1 1.6 1.8 2 .3-2.5 1-5 1.6-6.9z"/>',
  fan: '<circle cx="12" cy="12" r="1.6"/><g><path d="M12 10.4c0-2.6-1.3-4.6-3.3-4.6-1.7 0-2.7 1.2-2.5 2.8.2 1.8 2.5 2.5 5.8 1.8z"/></g><g transform="rotate(120 12 12)"><path d="M12 10.4c0-2.6-1.3-4.6-3.3-4.6-1.7 0-2.7 1.2-2.5 2.8.2 1.8 2.5 2.5 5.8 1.8z"/></g><g transform="rotate(240 12 12)"><path d="M12 10.4c0-2.6-1.3-4.6-3.3-4.6-1.7 0-2.7 1.2-2.5 2.8.2 1.8 2.5 2.5 5.8 1.8z"/></g>',
  wind: '<path d="M3.6 8.6h9.2a2.3 2.3 0 1 0-2.3-2.3"/><path d="M3.6 12.9h13a2.6 2.6 0 1 1-2.6 2.6"/><path d="M3.6 17h6.8"/>',
  'air-in': '<path d="M17.6 4.6v14.8"/><path d="M4.4 12h9.6"/><path d="M10.6 8.6 14 12l-3.4 3.4"/>',
  'air-out': '<path d="M6.4 4.6v14.8"/><path d="M10 12h9.6"/><path d="M16.2 8.6 19.6 12l-3.4 3.4"/>',
  filter: '<rect x="4.4" y="7" width="15.2" height="10" rx="2"/><path d="M8.6 7v10M12 7v10M15.4 7v10"/>',
  bypass:
    '<path d="M3.4 16.4h1.9c2.2 0 3-1.3 3.6-2.6.6-1.3 1.3-2.6 3.5-2.6h4.3"/><path d="M14.5 8.9l2.4 2.3-2.4 2.3"/><rect x="7.6" y="13.6" width="8.6" height="4.6" rx="1.5"/>',
  steam:
    '<path d="M9.3 4.3c1.9 1.5-1.9 3.2 0 4.7s-1.9 3.2 0 4.7-1.9 3.2 0 4.7"/><path d="M14.7 4.3c1.9 1.5-1.9 3.2 0 4.7s-1.9 3.2 0 4.7-1.9 3.2 0 4.7"/>',
  drop: '<path d="M12 3.9s5 5.4 5 9a5 5 0 1 1-10 0c0-3.6 5-9 5-9z"/>',
  waves:
    '<path d="M3.8 8c1.6-1.6 3.4-1.6 5 0s3.4 1.6 5 0 3.4-1.6 5 0"/><path d="M3.8 13c1.6-1.6 3.4-1.6 5 0s3.4 1.6 5 0 3.4-1.6 5 0"/><path d="M3.8 18c1.6-1.6 3.4-1.6 5 0s3.4 1.6 5 0 3.4-1.6 5 0" opacity=".5"/>',
  radiator:
    '<rect x="4.6" y="6.6" width="14.8" height="10.6" rx="1.8"/><path d="M8.6 10v3.8M12 10v3.8M15.4 10v3.8"/><path d="M7.4 17.2v2.4M16.6 17.2v2.4"/>',
  eco: '<path d="M19.3 4.7C11.3 5 6.2 8.4 6.2 13.2a4.6 4.6 0 0 0 4.6 4.6c4.8 0 8.5-5.1 8.5-13.1z"/><path d="M7.8 16.2c2.3-2.7 5.2-4.9 8.5-6.4"/>',
  lock: '<rect x="6.5" y="10.6" width="11" height="9" rx="2"/><path d="M9.3 10.6V8.3a2.7 2.7 0 0 1 5.4 0v2.3"/><path d="M12 14.3v1.9"/>',
  unlock:
    '<rect x="6.5" y="10.6" width="11" height="9" rx="2"/><path d="M9.3 10.6V8.3a2.7 2.7 0 0 1 5.3-.7"/><path d="M12 14.3v1.9"/>',
  // Access — the garage door's own pair (the family's widget, 2026-10-16: a
  // door is neither a lock nor a shade): the closed sectional leaf, and the
  // leaf swung open in its frame.
  garage:
    '<rect x="4.4" y="7.8" width="15.2" height="11.8" rx="1.6"/><path d="M4.4 11.7h15.2M4.4 15.6h15.2"/><path d="M2.9 7.8 12 3.3l9.1 4.5"/>',
  door_open:
    '<path d="M4.8 20.4V4.2h8.1v16.2"/><path d="M12.9 20.4 19.6 17.9V6.6L12.9 4.1z"/>',
  shield: '<path d="M12 3.7 19 6.3v5.1c0 4.2-2.8 7.7-7 9-4.2-1.3-7-4.8-7-9V6.3z"/>',
  'shield-off': '<path d="M12 3.7 19 6.3v5.1c0 4.2-2.8 7.7-7 9-4.2-1.3-7-4.8-7-9V6.3z"/><path d="M4.9 4.9 19.1 19.1"/>',
  bell: '<path d="M6.7 17.3h10.6c-.9-.7-1.3-1.7-1.3-3v-3.6a4 4 0 1 0-8 0v3.6c0 1.3-.4 2.3-1.3 3z"/><path d="M10.3 19.7a1.9 1.9 0 0 0 3.4 0"/>',
  eye: '<path d="M3 12s3.2-5 9-5 9 5 9 5-3.2 5-9 5-9-5-9-5z"/><circle cx="12" cy="12" r="2.6"/>',
  key: '<circle cx="8" cy="12" r="3.1"/><path d="M11.1 12h9"/><path d="M17 12v2.7M20.1 12v1.9"/>',
  person: '<circle cx="12" cy="7.9" r="2.6"/><path d="M6.6 19.3c.8-3.3 2.8-5 5.4-5s4.6 1.7 5.4 5"/>',
  play: '<path d="M8.9 6.7v10.6L17.4 12z"/>',
  pause: '<path d="M9.5 7v10M14.5 7v10"/>',
  'skip-back': '<path d="M6.4 6.9v10.2"/><path d="M18.5 6.9v10.2L10.2 12z"/>',
  'skip-fwd': '<path d="M17.6 6.9v10.2"/><path d="M5.5 6.9v10.2L13.8 12z"/>',
  volume:
    '<path d="M4.7 9.7h2.7l3.7-3.3v11.2l-3.7-3.3H4.7z"/><path d="M14.4 9.7a3.5 3.5 0 0 1 0 4.6"/><path d="M16.7 7.8a6.7 6.7 0 0 1 0 8.4"/>',
  'volume-mute': '<path d="M4.7 9.7h2.7l3.7-3.3v11.2l-3.7-3.3H4.7z"/><path d="M15 9.9l4.2 4.2M19.2 9.9 15 14.1"/>',
  pin: '<path d="M12 21s6.2-5.5 6.2-10.1a6.2 6.2 0 1 0-12.4 0C5.8 15.5 12 21 12 21z"/><circle cx="12" cy="10.7" r="2.2"/>',
  clock: '<circle cx="12" cy="12" r="8.1"/><path d="M12 7.5V12l3 1.8"/>',
  bolt: '<path d="M13.1 3.5 6.7 13.1h4.5L10.5 20.5 16.9 10.9h-4.5z"/>',
  activity: '<path d="M3.6 12.4h2.9l2-5 3.1 9.4 2.2-5.9 1.4 1.5h5.2"/>',
  timer:
    '<path d="M7.6 4.4h8.8M7.6 19.6h8.8"/><path d="M8.8 4.4c0 3.5 6.4 3.8 6.4 7.4s-6.4 3.9-6.4 7.4"/><path d="M15.2 4.4c0 3.5-6.4 3.8-6.4 7.4s6.4 3.9 6.4 7.4"/>',
  camera:
    '<path d="M4.5 8.6h2.3l1.5-2.2h7.4l1.5 2.2h2.3a1.6 1.6 0 0 1 1.6 1.6v6.9a1.6 1.6 0 0 1-1.6 1.6H4.5a1.6 1.6 0 0 1-1.6-1.6v-6.9a1.6 1.6 0 0 1 1.6-1.6z"/><circle cx="12" cy="13.3" r="3.1"/>',
  computer: '<rect x="3.7" y="5.2" width="16.6" height="11" rx="1.8"/><path d="M12 16.2v3.4M8.6 19.6h6.8"/>',
  server:
    '<rect x="4.5" y="4.8" width="15" height="6.4" rx="1.8"/><rect x="4.5" y="12.8" width="15" height="6.4" rx="1.8"/><path d="M7.6 8h.01M7.6 16h.01"/>',
  // The loading state (card t_bd3aea71): a 270° arc; callers spin it with
  // `animate-spin`. In the TREE AT RUNTIME, not in the sketch — see the header.
  spinner: '<path d="M12 3.8a8.2 8.2 0 1 1-8.2 8.2"/>',
  // Drawn for the in-sheet automation & sun panels (card t_a8249a54) — see the
  // header. A physical switch (the external-control automations), a cycle /
  // reset (one-button cycles, the sun cell's reset), up/down buttons, a press
  // (momentary modes), a follow link, absence and sleep.
  toggle: '<rect x="3.6" y="8.3" width="16.8" height="7.4" rx="3.7"/><circle cx="15.5" cy="12" r="2.2"/>',
  refresh: '<path d="M16.9 6.9A7.4 7.4 0 1 1 13.7 4.75"/><path d="M11.1 3.15l3 .85-.85 3"/>',
  'up-down': '<path d="M12 4.8v14.4"/><path d="M9.3 7.5 12 4.8l2.7 2.7"/><path d="M9.3 16.5 12 19.2l2.7-2.7"/>',
  tap: '<path d="M10.6 13.4V6.1a1.4 1.4 0 0 1 2.8 0v5.3"/><path d="M10.6 9.2H9.1a1.3 1.3 0 0 0-1.3 1.3v3.1a5 5 0 0 0 5 5h1.2a5 5 0 0 0 5-5v-1.9a1.5 1.5 0 0 0-3 0"/>',
  link: '<path d="M9.5 14.5 7.1 16.9a3.3 3.3 0 0 1-4.6-4.7l2.4-2.4"/><path d="M14.5 9.5l2.4-2.4a3.3 3.3 0 0 1 4.6 4.7l-2.4 2.4"/><path d="M9.9 14.1l4.2-4.2"/>',
  'person-off':
    '<circle cx="12" cy="7.9" r="2.6"/><path d="M6.6 19.3c.8-3.3 2.8-5 5.4-5s4.6 1.7 5.4 5"/><path d="M4.8 4.8 19.2 19.2"/>',
  moon: '<path d="M20.4 14.1A8.3 8.3 0 0 1 9.9 3.6a8.3 8.3 0 1 0 10.5 10.5z"/>',
};

/** The sketch's `GROUPS`: group title → the icons it renders, in order. */
export const CONTROL_ICON_GROUPS: Record<string, readonly string[]> = {
  'Power & controls': [
    'power',
    'power-off',
    'plus',
    'minus',
    'chevron-up',
    'chevron-down',
    'gear',
    'close',
    'toggle',
    'up-down',
    'tap',
  ],
  Light: ['bulb', 'warm', 'snowflake', 'palette', 'sparkles'],
  Shades: ['shade', 'cover-open', 'cover-close', 'stop', 'tilt', 'rotate-ccw', 'rotate-cw'],
  'Climate & air': ['thermometer', 'flame', 'fan', 'wind', 'air-in', 'air-out', 'filter', 'bypass', 'radiator', 'eco'],
  'Water & wellness': ['steam', 'drop', 'waves'],
  Access: ['garage', 'door_open'],
  'Security & presence': [
    'lock',
    'unlock',
    'shield',
    'shield-off',
    'bell',
    'eye',
    'key',
    'person',
    'person-off',
    'moon',
  ],
  Media: ['play', 'pause', 'skip-back', 'skip-fwd', 'volume', 'volume-mute'],
  'System & chrome': [
    'pin',
    'clock',
    'timer',
    'bolt',
    'activity',
    'camera',
    'computer',
    'server',
    'spinner',
    'refresh',
    'link',
  ],
};
